import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminGet } from './adminApi';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

const GREEN = '#0B6B55';
const mono = { fontFamily: "'IBM Plex Mono', monospace" };
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const RESULT_BADGE = {
  WITHIN_LIMITS: { text: 'Within limits', color: GREEN },
  EXCURSION: { text: 'Excursion', color: '#8C2B2B', bg: '#FBEBEB' },
  UNMONITORED: { text: 'Unmonitored', color: '#6b7069', bg: '#EFEDE6' },
  LIVE: { text: 'Live', color: GREEN },
  HIDDEN_PASS: { text: 'Pass', color: GREEN },
  HIDDEN_FAIL: { text: 'Fail', color: '#8C2B2B' },
};

function PassportJourney({ passportId }) {
  const [j, setJ] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    adminGet(`/passports/${passportId}/journey`).then(setJ).catch(e => toast.error(e.message));
  }, [passportId]);
  if (!j) return <p className="text-sm p-8" style={{ color: '#9a9e96' }}>Loading…</p>;

  return (
    <div className="flex" data-testid="passport-journey-page">
      <div className="flex-1 p-8 min-w-0">
        <button data-testid="back-to-passports" onClick={() => navigate('/admin/passports')}
          className="flex items-center gap-1.5 text-sm mb-3 hover:underline" style={{ color: '#6b7069' }}>
          <ArrowLeft size={15} /> Passports</button>
        <p className="text-sm" style={{ color: '#6b7069' }}>Passports · {j.template_name || '—'}</p>
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-4xl font-bold" style={{ ...mono, color: '#1B1D1C' }}>{j.short_code}</h1>
          <span className="px-2 py-0.5 rounded text-sm" style={
            j.state === 'ACTIVE' ? { background: '#E7F2EE', color: GREEN }
              : j.state === 'CLOSED' ? { background: '#EFEDE6', color: '#6b7069' }
                : { background: '#FBEBEB', color: '#8C2B2B' }}>{j.state}</span>
        </div>
        <p className="text-sm mb-2" style={{ color: '#9a9e96' }}>
          {j.product ? `${j.product.product_name || ''} · Batch ${j.product.batch_no || '—'}` : 'Logistics-only passport'}
          {j.kind === 'CARTON' && <span className="ml-2 px-2 py-0.5 rounded text-xs" style={{ background: '#EEF3FB', color: '#2B4E8C' }} data-testid="carton-badge">Carton · {j.packs_count} pack(s)</span>}
        </p>
        {j.carton_note && <p className="text-xs mb-4 px-2 py-1 rounded inline-block" style={{ background: '#FBF3E6', color: '#8C6A2B' }} data-testid="carton-note">{j.carton_note} — journey shown from the carton</p>}

        <div className="rounded-lg border bg-white p-5" style={{ borderColor: '#E5E3DC' }}>
          <h2 className="font-bold text-lg mb-3" style={{ color: '#1B1D1C' }}>Journey legs</h2>
          <table className="w-full text-sm">
            <thead><tr className="text-left border-b" style={{ color: '#6b7069', borderColor: '#EDEBE4' }}>
              <th className="py-2 font-medium">Stage</th><th className="font-medium">Custodian</th>
              <th className="font-medium">Device</th><th className="font-medium">In</th>
              <th className="font-medium">Out</th><th className="font-medium">Min / max</th>
              <th className="font-medium">Result</th></tr></thead>
            <tbody>
              {j.legs.map((l, i) => {
                const b = RESULT_BADGE[l.result] || RESULT_BADGE.LIVE;
                return (
                  <tr key={l.id || `${l.stage_name}-${l.started_at || i}`} className="border-b" style={{ borderColor: '#F3F1EA' }} data-testid={`leg-row-${i}`}>
                    <td className="py-3">{l.stage_name}</td>
                    <td>{l.custodian}</td>
                    <td style={mono}>{l.track0tic_id || 'None'}</td>
                    <td className="text-xs">{l.started_at ? new Date(l.started_at).toLocaleString() : '—'}</td>
                    <td className="text-xs">{l.ended_at ? new Date(l.ended_at).toLocaleString() : <span style={{ color: GREEN }}>Now</span>}</td>
                    <td style={mono}>{l.temp_min_seen != null ? `${l.temp_min_seen} / ${l.temp_max_seen} °C` : '—'}</td>
                    <td>{b.bg
                      ? <span className="px-2 py-0.5 rounded text-xs" style={{ background: b.bg, color: b.color }}>
                        {b.text}{l.result === 'EXCURSION' ? ` · ${l.excursion_minutes} min` : ''}</span>
                      : <span className="text-sm" style={{ color: b.color }}>{b.text}</span>}</td>
                  </tr>
                );
              })}
              {j.legs.length === 0 && <tr><td colSpan={7} className="py-6 text-center" style={{ color: '#9a9e96' }}>No legs yet</td></tr>}
            </tbody>
          </table>
          <p className="text-xs mt-3" style={{ color: '#9a9e96' }}>
            Each leg's telemetry comes only from that leg's device, for that leg's time window. A handoff = scan the next device QR, then this passport.</p>
        </div>
      </div>

      <div className="w-80 shrink-0 border-l p-6" style={{ borderColor: '#E5E3DC' }}>
        <h2 className="font-bold text-lg mb-3" style={{ color: '#1B1D1C' }}>Certificate summary</h2>
        <div className="rounded-lg border bg-white p-4 text-sm space-y-2" style={{ borderColor: '#E5E3DC' }} data-testid="certificate-summary">
          <div className="flex justify-between"><span style={{ color: '#6b7069' }}>Legs monitored</span>
            <b>{j.summary.legs_monitored} of {j.summary.legs_total}</b></div>
          <div className="flex justify-between"><span style={{ color: '#6b7069' }}>Excursions</span>
            <b style={{ color: j.summary.excursions ? '#8C2B2B' : '#1B1D1C' }}>
              {j.summary.excursions}{j.summary.excursions ? ` · ${j.summary.excursion_minutes} min` : ''}</b></div>
          <div className="flex justify-between"><span style={{ color: '#6b7069' }}>Unmonitored time</span>
            <b>{j.summary.unmonitored_minutes} min</b></div>
        </div>
        {j.state === 'CLOSED' && (
          <a href={`${API_BASE}/api/admin/passports/${passportId}/certificate`} target="_blank" rel="noreferrer"
            data-testid="download-certificate-btn"
            className="block mt-4 rounded-md py-2 text-center text-sm text-white" style={{ background: GREEN }}>
            Download certificate</a>
        )}
        <p className="text-xs mt-4" style={{ color: '#6b7069' }}>
          The certificate shows every leg honestly, including unmonitored ones. It never fills gaps with guessed data.</p>
        <h2 className="font-bold text-lg mt-6 mb-3" style={{ color: '#1B1D1C' }}>Who sees what</h2>
        {[['Each company in the chain', 'Full detail for its own legs; pass / fail only for others.'],
          ['Passport owner (brand)', 'Whole chain, subject to agreement with partners.'],
          ['Public scan', 'Product status and certificate. No locations, no company internals.']].map(([t, s]) => (
          <div key={t} className="rounded-lg border bg-white p-3 mb-2" style={{ borderColor: '#E5E3DC' }}>
            <p className="text-sm font-bold" style={{ color: '#1B1D1C' }}>{t}</p>
            <p className="text-xs" style={{ color: '#6b7069' }}>{s}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminPassportsPage() {
  const { passportId } = useParams();
  const [rows, setRows] = useState([]);
  const [state, setState] = useState('');
  const [q, setQ] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (passportId) return;
    const qs = new URLSearchParams();
    if (state) qs.set('state', state);
    if (q) qs.set('q', q);
    adminGet(`/passports?${qs}`).then(setRows).catch(e => toast.error(e.message));
  }, [passportId, state, q]);

  if (passportId) return <PassportJourney passportId={passportId} />;

  return (
    <div className="p-8" data-testid="admin-passports-page">
      <h1 className="text-4xl font-bold mb-5" style={{ color: '#1B1D1C' }}>Passports</h1>
      <div className="flex gap-3 mb-4">
        <input data-testid="passports-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search label code…"
          className="h-9 px-3 rounded-md border bg-white text-sm w-72" style={{ borderColor: '#D9D6CD' }} />
        <select data-testid="passports-state-filter" value={state} onChange={e => setState(e.target.value)}
          className="h-9 px-3 rounded-md border bg-white text-sm" style={{ borderColor: '#D9D6CD' }}>
          <option value="">All states</option>
          {['DORMANT', 'ACTIVE', 'CLOSED', 'EXPIRED', 'VOIDED'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="rounded-lg border bg-white" style={{ borderColor: '#E5E3DC' }}>
        <table className="w-full text-sm">
          <thead><tr className="text-left border-b" style={{ color: '#6b7069', borderColor: '#EDEBE4' }}>
            <th className="py-3 px-4 font-medium">Label</th><th className="font-medium">Customer</th>
            <th className="font-medium">Product</th><th className="font-medium">Activated</th>
            <th className="font-medium">Closed</th><th className="font-medium pr-4">State</th></tr></thead>
          <tbody>
            {rows.map(p => (
              <tr key={p.id} data-testid={`passport-row-${p.short_code}`} className="border-b cursor-pointer hover:bg-[#FAF9F6]"
                style={{ borderColor: '#F3F1EA' }} onClick={() => navigate(`/admin/passports/${p.id}`)}>
                <td className="py-3 px-4" style={mono}>{p.short_code}</td>
                <td>{p.customer}</td>
                <td>{p.product_name || <span style={{ color: '#9a9e96' }}>—</span>}</td>
                <td className="text-xs">{p.activated_at ? new Date(p.activated_at).toLocaleString() : '—'}</td>
                <td className="text-xs">{p.closed_at ? new Date(p.closed_at).toLocaleString() : '—'}</td>
                <td className="pr-4"><span className="px-2 py-0.5 rounded text-xs" style={
                  p.state === 'ACTIVE' ? { background: '#E7F2EE', color: GREEN }
                    : p.state === 'DORMANT' ? { background: '#FAF9F6', color: '#6b7069', border: '1px solid #E5E3DC' }
                      : p.state === 'CLOSED' ? { background: '#EFEDE6', color: '#6b7069' }
                        : { background: '#FBEBEB', color: '#8C2B2B' }}>{p.state}</span></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center" style={{ color: '#9a9e96' }}>No passports</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
